import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  ComposedChart,
} from "recharts";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Globe,
  ArrowLeft,
  Info,
  DollarSign,
  Anchor,
  BarChart3,
  RefreshCw,
  MapPin,
  Fuel,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";

// ── Static fallback data (used when live API is unavailable) ──────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const FUEL_PRICES_API = `${API_BASE}/api/fuel-prices/`;
const KENYA_PRICES_API = `${API_BASE}/api/kenya-fuel-prices/`;
const WORLD_PRICES_API = `${API_BASE}/api/world-fuel-prices/`;

// TopoJSON for world map (from world-atlas CDN – loaded in browser at runtime)
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const staticFuelPrices = [
  { month: "Jan 2023", brent: 83.9, wti: 78.1, naturalGas: 18.4 },
  { month: "Apr 2023", brent: 79.5, wti: 74.2, naturalGas: 14.2 },
  { month: "Jul 2023", brent: 74.9, wti: 70.8, naturalGas: 9.8 },
  { month: "Oct 2023", brent: 91.3, wti: 86.5, naturalGas: 12.1 },
  { month: "Jan 2024", brent: 77.4, wti: 72.9, naturalGas: 10.3 },
  { month: "Apr 2024", brent: 89.0, wti: 84.3, naturalGas: 9.4 },
  { month: "Jul 2024", brent: 82.7, wti: 78.2, naturalGas: 8.7 },
  { month: "Oct 2024", brent: 75.8, wti: 71.1, naturalGas: 10.5 },
  { month: "Jan 2025", brent: 79.2, wti: 74.8, naturalGas: 11.2 },
  { month: "Apr 2025", brent: 72.1, wti: 68.4, naturalGas: 9.8 },
];

const regionalPumpPrices = [
  { region: "USA", price: 3.5, color: "#3b82f6" },
  { region: "Europe", price: 6.8, color: "#6366f1" },
  { region: "Middle East", price: 1.2, color: "#10b981" },
  { region: "Africa", price: 4.1, color: "#f59e0b" },
  { region: "Asia-Pacific", price: 5.3, color: "#ef4444" },
  { region: "Latin America", price: 4.7, color: "#8b5cf6" },
];

const economicImpactData = [
  { sector: "Transport", impact: -18, jobs: -2.1 },
  { sector: "Agriculture", impact: -12, jobs: -1.4 },
  { sector: "Manufacturing", impact: -9, jobs: -0.8 },
  { sector: "Retail", impact: -7, jobs: -0.6 },
  { sector: "Energy", impact: 24, jobs: 1.2 },
  { sector: "Logistics", impact: -21, jobs: -2.8 },
];

// Projection data: open vs closed Strait of Hormuz
const straitsProjection = [
  {
    month: "May 2025",
    open: 72,
    closed: 72,
    baseline: 72,
  },
  {
    month: "Jun 2025",
    open: 74,
    closed: 88,
    baseline: 73,
  },
  {
    month: "Jul 2025",
    open: 75,
    closed: 105,
    baseline: 73,
  },
  {
    month: "Aug 2025",
    open: 76,
    closed: 118,
    baseline: 74,
  },
  {
    month: "Sep 2025",
    open: 77,
    closed: 130,
    baseline: 74,
  },
  {
    month: "Oct 2025",
    open: 78,
    closed: 145,
    baseline: 75,
  },
  {
    month: "Nov 2025",
    open: 76,
    closed: 160,
    baseline: 75,
  },
  {
    month: "Dec 2025",
    open: 75,
    closed: 172,
    baseline: 76,
  },
];

const gdpImpactProjection = [
  { region: "Global", open: -0.3, closed: -2.8 },
  { region: "Europe", open: -0.6, closed: -4.1 },
  { region: "Asia", open: -0.8, closed: -5.2 },
  { region: "USA", open: -0.2, closed: -1.9 },
  { region: "Africa", open: -1.1, closed: -3.6 },
  { region: "Mideast", open: 1.4, closed: -6.8 },
];

const inflationProjection = [
  { region: "Global", open: 0.4, closed: 3.8 },
  { region: "Europe", open: 0.7, closed: 5.1 },
  { region: "Asia", open: 0.9, closed: 6.3 },
  { region: "USA", open: 0.3, closed: 2.4 },
  { region: "Africa", open: 1.2, closed: 7.1 },
  { region: "Mideast", open: 0.5, closed: 4.9 },
];

// ── World heatmap helpers ──────────────────────────────────────────────────────

// Map ISO numeric → ISO alpha-3 for countries included in our price dataset
// (world-atlas uses ISO 3166-1 numeric codes)
const ISO_NUM_TO_ALPHA3 = {
  "404": "KEN", "834": "TZA", "800": "UGA", "646": "RWA", "231": "ETH",
  "710": "ZAF", "288": "GHA", "566": "NGA", "818": "EGY", "504": "MAR",
  "788": "TUN", "012": "DZA", "434": "LBY", "729": "SDN", "716": "ZWE",
  "508": "MOZ", "894": "ZMB", "454": "MWI", "120": "CMR", "384": "CIV",
  "686": "SEN", "450": "MDG", "024": "AGO", "180": "COD",
  "682": "SAU", "784": "UAE", "634": "QAT", "414": "KWT", "048": "BHR",
  "512": "OMN", "368": "IRQ", "364": "IRN", "887": "YEM", "400": "JOR",
  "376": "ISR", "422": "LBN", "760": "SYR",
  "156": "CHN", "356": "IND", "392": "JPN", "410": "KOR", "036": "AUS",
  "554": "NZL", "360": "IDN", "458": "MYS", "702": "SGP", "764": "THA",
  "704": "VNM", "608": "PHL", "050": "BGD", "586": "PAK", "144": "LKA",
  "104": "MMR", "398": "KAZ", "860": "UZB", "004": "AFG", "524": "NPL",
  "158": "TWN", "496": "MNG",
  "826": "GBR", "276": "DEU", "250": "FRA", "528": "NLD", "056": "BEL",
  "724": "ESP", "380": "ITA", "752": "SWE", "578": "NOR", "246": "FIN",
  "208": "DNK", "756": "CHE", "040": "AUT", "620": "PRT", "300": "GRC",
  "616": "POL", "203": "CZE", "348": "HUN", "642": "ROU", "100": "BGR",
  "703": "SVK", "191": "HRV", "688": "SRB", "804": "UKR", "643": "RUS",
  "792": "TUR", "372": "IRL", "442": "LUX",
  "840": "USA", "124": "CAN", "484": "MEX", "076": "BRA", "032": "ARG",
  "152": "CHL", "170": "COL", "604": "PER", "218": "ECU", "862": "VEN",
  "068": "BOL", "600": "PRY", "858": "URY", "320": "GTM", "192": "CUB",
  "214": "DOM", "388": "JAM",
};

/**
 * Map a USD/L pump price to a fill colour.
 * Cheap (<0.5) → deep green; expensive (>2.0) → deep red; unknown → light grey.
 */
function priceToColor(price) {
  if (price === null || price === undefined) return "#e2e8f0";
  if (price < 0.50) return "#059669"; // emerald-600
  if (price < 0.80) return "#10b981"; // emerald-500
  if (price < 1.10) return "#34d399"; // emerald-400
  if (price < 1.30) return "#fbbf24"; // amber-400
  if (price < 1.60) return "#f97316"; // orange-500
  if (price < 1.90) return "#ef4444"; // red-500
  return "#991b1b";                   // red-800
}

const MapLegend = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 mt-3">
    <span className="font-medium text-slate-700">USD/L:</span>
    {[
      { label: "<$0.50", color: "#059669" },
      { label: "$0.50–0.80", color: "#10b981" },
      { label: "$0.80–1.10", color: "#34d399" },
      { label: "$1.10–1.30", color: "#fbbf24" },
      { label: "$1.30–1.60", color: "#f97316" },
      { label: "$1.60–1.90", color: "#ef4444" },
      { label: ">$1.90", color: "#991b1b" },
      { label: "No data", color: "#e2e8f0" },
    ].map(({ label, color }) => (
      <span key={label} className="flex items-center gap-1">
        <span
          className="inline-block w-3 h-3 rounded-sm border border-slate-200"
          style={{ background: color }}
        />
        {label}
      </span>
    ))}
  </div>
);

// ── Helper components ──────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 flex gap-4 items-start">
    <div className={`p-3 rounded-lg ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-slate-500 text-sm">{label}</p>
      <p className="text-2xl font-bold text-slate-800 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionTitle = ({ children, sub }) => (
  <div className="mb-6">
    <h2 className="text-xl font-bold text-slate-800">{children}</h2>
    {sub && <p className="text-slate-500 text-sm mt-1">{sub}</p>}
  </div>
);

const ScenarioBadge = ({ scenario }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
      scenario === "open"
        ? "bg-emerald-100 text-emerald-700"
        : "bg-red-100 text-red-700"
    }`}
  >
    {scenario === "open" ? (
      <Anchor className="w-3 h-3" />
    ) : (
      <AlertTriangle className="w-3 h-3" />
    )}
    Hormuz {scenario === "open" ? "Open" : "Closed"}
  </span>
);

const CustomTooltip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name}: <span className="font-bold">{entry.value}{unit}</span>
        </p>
      ))}
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

export default function FuelCrisisPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  // ── Global crude prices state ───────────────────────────────────────────────
  const [priceData, setPriceData] = useState(staticFuelPrices);
  const [dataSource, setDataSource] = useState("static");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // ── Kenya prices state ──────────────────────────────────────────────────────
  const [kenyaData, setKenyaData] = useState([]);
  const [kenyaSource, setKenyaSource] = useState("loading");
  const [kenyaLoading, setKenyaLoading] = useState(true);

  // ── World heatmap state ─────────────────────────────────────────────────────
  const [worldPrices, setWorldPrices] = useState({});
  const [mapTooltip, setMapTooltip] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(FUEL_PRICES_API)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        if (json.data && json.data.length > 0) {
          setPriceData(json.data);
          setDataSource(json.source || "live");
          setUpdatedAt(json.updated_at ? new Date(json.updated_at * 1000) : new Date());
          setFetchError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFetchError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(KENYA_PRICES_API)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json.data?.length) {
          setKenyaData(json.data);
          setKenyaSource(json.source || "epra_static");
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setKenyaLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetch(WORLD_PRICES_API)
      .then((r) => r.json())
      .then((json) => { if (json.data) setWorldPrices(json.data); })
      .catch(() => {});
  }, []);

  // Derived values from the latest available data point
  const latestEntry = priceData[priceData.length - 1] ?? {};
  const prevYearEntry = priceData[priceData.length - 13] ?? {};
  const brentYoY =
    latestEntry.brent && prevYearEntry.brent
      ? (((latestEntry.brent - prevYearEntry.brent) / prevYearEntry.brent) * 100).toFixed(0)
      : null;
  const wtiYoY =
    latestEntry.wti && prevYearEntry.wti
      ? (((latestEntry.wti - prevYearEntry.wti) / prevYearEntry.wti) * 100).toFixed(0)
      : null;

  // Kenya derived stats
  const kLatest = kenyaData[kenyaData.length - 1] ?? {};
  const kPrev = kenyaData[kenyaData.length - 2] ?? {};
  const kPetrolChange = kLatest.petrol && kPrev.petrol
    ? (kLatest.petrol - kPrev.petrol).toFixed(2)
    : null;
  const kDieselChange = kLatest.diesel && kPrev.diesel
    ? (kLatest.diesel - kPrev.diesel).toFixed(2)
    : null;

  const tabs = [
    { id: "overview", label: "Global Overview", icon: Globe },
    { id: "kenya", label: "Kenya", icon: MapPin },
    { id: "hormuz", label: "Strait of Hormuz", icon: Anchor },
    { id: "economy", label: "Economic Impact", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-4 sm:px-6 lg:px-8 py-5 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition"
            aria-label="Back to home"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Global Fuel Price Crisis</h1>
            <p className="text-slate-400 text-sm">
              Interactive analysis · Economic projections · Strait of Hormuz scenarios
            </p>
          </div>
          {/* Live data indicator */}
          <div className="hidden sm:flex items-center gap-2 text-xs">
            {loading ? (
              <span className="flex items-center gap-1.5 text-slate-400">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Fetching live prices…
              </span>
            ) : fetchError ? (
              <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 font-medium">
                Static data (API unavailable)
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live · {dataSource === "eia" ? "EIA" : "World Bank"}
                {updatedAt && (
                  <span className="text-slate-400 font-normal">
                    &nbsp;· Updated {updatedAt.toLocaleDateString()}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {/* ── OVERVIEW TAB ── */}
        {activeTab === "overview" && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={DollarSign}
                label={`Brent Crude (${latestEntry.month ?? "latest"})`}
                value={latestEntry.brent ? `$${latestEntry.brent} / bbl` : "—"}
                sub={
                  brentYoY !== null
                    ? `${brentYoY > 0 ? "↑" : "↓"} ${Math.abs(brentYoY)}% YoY`
                    : "YoY change unavailable"
                }
                color="bg-blue-500"
              />
              <StatCard
                icon={wtiYoY !== null && wtiYoY > 0 ? TrendingUp : TrendingDown}
                label={`WTI Crude (${latestEntry.month ?? "latest"})`}
                value={latestEntry.wti ? `$${latestEntry.wti} / bbl` : "—"}
                sub={
                  wtiYoY !== null
                    ? `${wtiYoY > 0 ? "↑" : "↓"} ${Math.abs(wtiYoY)}% YoY`
                    : "YoY change unavailable"
                }
                color="bg-indigo-500"
              />
              <StatCard
                icon={AlertTriangle}
                label="Countries in Fuel Crisis"
                value="38"
                sub="As of Q1 2025"
                color="bg-amber-500"
              />
              <StatCard
                icon={TrendingUp}
                label="Global Avg Pump Price"
                value="$4.27 / L"
                sub="+6% vs 2023"
                color="bg-emerald-500"
              />
            </div>

            {/* Crude oil price trend */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <SectionTitle
                sub="Brent crude, WTI and natural gas spot prices (USD)"
              >
                Crude Oil &amp; Gas Price Trend
              </SectionTitle>
              {loading ? (
                <div className="flex items-center justify-center h-[300px] text-slate-400 gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Loading live price data…</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={priceData}>
                    <defs>
                      <linearGradient id="brentGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="wtiGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gasGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip unit=" USD" />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="brent"
                      name="Brent ($/bbl)"
                      stroke="#3b82f6"
                      fill="url(#brentGrad)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="wti"
                      name="WTI ($/bbl)"
                      stroke="#6366f1"
                      fill="url(#wtiGrad)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="naturalGas"
                      name="Nat. Gas ($/MMBtu)"
                      stroke="#10b981"
                      fill="url(#gasGrad)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Regional pump prices */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <SectionTitle sub="Average pump price per litre (USD) by region, Apr 2025">
                Regional Pump Prices
              </SectionTitle>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={regionalPumpPrices} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    dataKey="region"
                    type="category"
                    width={90}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className="bg-white border border-slate-200 rounded-lg shadow p-3 text-sm">
                          <p className="font-semibold text-slate-700">{label}</p>
                          <p style={{ color: payload[0].fill }}>
                            ${payload[0].value.toFixed(2)} / L
                          </p>
                        </div>
                      ) : null
                    }
                  />
                  <Bar dataKey="price" name="Price ($/L)" radius={[0, 4, 4, 0]}>
                    {regionalPumpPrices.map((entry) => (
                      <Cell key={entry.region} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* World heatmap */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <SectionTitle sub="Average retail pump price per litre (USD) · hover a country for details">
                World Fuel Price Heat Map
              </SectionTitle>
              <div className="relative" style={{ background: "#f8fafc", borderRadius: 8, overflow: "hidden" }}>
                {mapTooltip && (
                  <div
                    className="pointer-events-none fixed z-50 bg-slate-800 text-white text-xs rounded-lg shadow-xl px-3 py-2"
                    style={{ left: mapTooltip.x + 12, top: mapTooltip.y - 10 }}
                  >
                    <p className="font-semibold">{mapTooltip.name}</p>
                    {mapTooltip.price !== null ? (
                      <p>${mapTooltip.price.toFixed(2)} / L</p>
                    ) : (
                      <p className="text-slate-400">No data</p>
                    )}
                  </div>
                )}
                <ComposableMap
                  projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
                  style={{ width: "100%", height: "auto" }}
                >
                  <ZoomableGroup>
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const numId = String(geo.id).padStart(3, "0");
                          const alpha3 = ISO_NUM_TO_ALPHA3[numId];
                          const price = alpha3 ? worldPrices[alpha3] : undefined;
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={priceToColor(price)}
                              stroke="#fff"
                              strokeWidth={0.4}
                              style={{
                                default: { outline: "none" },
                                hover: { outline: "none", opacity: 0.8 },
                                pressed: { outline: "none" },
                              }}
                              onMouseEnter={(e) => {
                                setMapTooltip({
                                  name: geo.properties.name,
                                  price: price ?? null,
                                  x: e.clientX,
                                  y: e.clientY,
                                });
                              }}
                              onMouseMove={(e) => {
                                setMapTooltip((prev) =>
                                  prev ? { ...prev, x: e.clientX, y: e.clientY } : prev
                                );
                              }}
                              onMouseLeave={() => setMapTooltip(null)}
                            />
                          );
                        })
                      }
                    </Geographies>
                  </ZoomableGroup>
                </ComposableMap>
              </div>
              <MapLegend />
            </div>

            {/* Insight banner */}
            <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-800">
              <Info className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-sm leading-relaxed">
                <strong>Data scientist note:</strong> The disparity in pump prices across
                regions is driven by domestic subsidies (Middle East), taxation policy
                (Europe), and supply-chain logistics (Africa). Despite falling crude
                benchmarks, consumer-facing prices have remained elevated due to
                refinery margin widening and currency depreciation in developing markets.
              </p>
            </div>
          </>
        )}

        {/* ── KENYA TAB ── */}
        {activeTab === "kenya" && (
          <>
            {/* Kenya KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Fuel}
                label={`Super Petrol (${kLatest.month ?? "latest"})`}
                value={kLatest.petrol ? `KES ${kLatest.petrol}` : "—"}
                sub={
                  kPetrolChange !== null
                    ? `${kPetrolChange > 0 ? "↑" : "↓"} KES ${Math.abs(kPetrolChange)} vs prev month`
                    : "Month-on-month change"
                }
                color="bg-blue-500"
              />
              <StatCard
                icon={kDieselChange !== null && kDieselChange > 0 ? TrendingUp : TrendingDown}
                label={`Diesel (${kLatest.month ?? "latest"})`}
                value={kLatest.diesel ? `KES ${kLatest.diesel}` : "—"}
                sub={
                  kDieselChange !== null
                    ? `${kDieselChange > 0 ? "↑" : "↓"} KES ${Math.abs(kDieselChange)} vs prev month`
                    : "Month-on-month change"
                }
                color="bg-indigo-500"
              />
              <StatCard
                icon={DollarSign}
                label={`Kerosene (${kLatest.month ?? "latest"})`}
                value={kLatest.kerosene ? `KES ${kLatest.kerosene}` : "—"}
                sub="Household cooking fuel"
                color="bg-amber-500"
              />
              <StatCard
                icon={Globe}
                label="Data source"
                value={kenyaSource === "epra_static" ? "EPRA" : "Live"}
                sub={
                  kenyaSource === "epra_static"
                    ? "Nairobi EPRA max pump prices"
                    : "GlobalPetrolPrices.com"
                }
                color="bg-emerald-500"
              />
            </div>

            {/* Kenya trend chart */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <SectionTitle sub="Nairobi EPRA maximum retail pump prices (KES/L) · Jan 2025 – present">
                Kenya Fuel Price Trends
              </SectionTitle>
              {kenyaLoading ? (
                <div className="flex items-center justify-center h-[320px] text-slate-400 gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Loading Kenya prices…</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={kenyaData}>
                    <defs>
                      <linearGradient id="petrolGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="dieselGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="keroGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={["auto", "auto"]}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `${v}`}
                      label={{ value: "KES/L", angle: -90, position: "insideLeft", fontSize: 11, fill: "#94a3b8", offset: 8 }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                            <p className="font-semibold text-slate-700 mb-1">{label}</p>
                            {payload.map((p) => (
                              <p key={p.name} style={{ color: p.stroke || p.color }}>
                                {p.name}: <strong>KES {p.value}</strong>/L
                              </p>
                            ))}
                          </div>
                        ) : null
                      }
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="petrol"
                      name="Super Petrol"
                      stroke="#3b82f6"
                      fill="url(#petrolGrad)"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#3b82f6" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="diesel"
                      name="Diesel"
                      stroke="#6366f1"
                      fill="url(#dieselGrad)"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#6366f1" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="kerosene"
                      name="Kerosene"
                      stroke="#f59e0b"
                      fill="url(#keroGrad)"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#f59e0b" }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Month-on-month change bar chart */}
            {kenyaData.length > 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
                <SectionTitle sub="Month-on-month change in Super Petrol price (KES/L)">
                  Monthly Price Change (Super Petrol)
                </SectionTitle>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={kenyaData.slice(1).map((d, i) => ({
                      month: d.month,
                      change: parseFloat(
                        (d.petrol - kenyaData[i].petrol).toFixed(2)
                      ),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis
                      tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}`}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <div className="bg-white border border-slate-200 rounded-lg shadow p-3 text-sm">
                            <p className="font-semibold text-slate-700">{label}</p>
                            <p style={{ color: payload[0].value >= 0 ? "#ef4444" : "#10b981" }}>
                              {payload[0].value >= 0 ? "+" : ""}{payload[0].value} KES/L
                            </p>
                          </div>
                        ) : null
                      }
                    />
                    <ReferenceLine y={0} stroke="#94a3b8" />
                    <Bar dataKey="change" name="Δ KES/L" radius={[4, 4, 0, 0]}>
                      {kenyaData.slice(1).map((d, i) => {
                        const change = d.petrol - kenyaData[i].petrol;
                        return (
                          <Cell
                            key={d.month}
                            fill={change >= 0 ? "#ef4444" : "#10b981"}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Kenya data table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <SectionTitle sub="Nairobi maximum pump prices set by EPRA (KES per litre)">
                  Monthly Price Data Table
                </SectionTitle>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-6 py-3">Month</th>
                      <th className="text-right px-6 py-3">Super Petrol (KES/L)</th>
                      <th className="text-right px-6 py-3">Diesel (KES/L)</th>
                      <th className="text-right px-6 py-3">Kerosene (KES/L)</th>
                      <th className="text-right px-6 py-3">Petrol MoM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...kenyaData].reverse().map((row, idx, arr) => {
                      const prevRow = arr[idx + 1];
                      const mom = prevRow ? parseFloat((row.petrol - prevRow.petrol).toFixed(2)) : null;
                      return (
                        <tr
                          key={row.month}
                          className={`hover:bg-slate-50 transition-colors ${
                            row.month === kLatest.month ? "bg-blue-50" : ""
                          }`}
                        >
                          <td className="px-6 py-3 font-medium text-slate-700">
                            {row.month}
                            {row.month === kLatest.month && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">
                                Latest
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-right text-slate-800 font-mono">
                            {row.petrol?.toFixed(2)}
                          </td>
                          <td className="px-6 py-3 text-right text-slate-800 font-mono">
                            {row.diesel?.toFixed(2)}
                          </td>
                          <td className="px-6 py-3 text-right text-slate-800 font-mono">
                            {row.kerosene?.toFixed(2)}
                          </td>
                          <td className="px-6 py-3 text-right">
                            {mom !== null ? (
                              <span
                                className={`inline-flex items-center gap-0.5 font-mono text-xs font-semibold ${
                                  mom > 0 ? "text-red-600" : mom < 0 ? "text-emerald-600" : "text-slate-400"
                                }`}
                              >
                                {mom > 0 ? (
                                  <ArrowUpRight className="w-3.5 h-3.5" />
                                ) : mom < 0 ? (
                                  <ArrowDownRight className="w-3.5 h-3.5" />
                                ) : null}
                                {mom > 0 ? "+" : ""}{mom}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Kenya context banner */}
            <div className="flex gap-3 bg-green-50 border border-green-200 rounded-xl p-4 text-green-800">
              <Info className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-sm leading-relaxed">
                <strong>Kenya fuel pricing:</strong> Pump prices in Kenya are regulated by
                the Energy and Petroleum Regulatory Authority (EPRA), which publishes
                maximum retail prices monthly, typically mid-month. Prices reflect
                international crude oil benchmarks (Murban crude), the Kenya shilling
                exchange rate, refinery costs, and government levies including road
                maintenance levy, excise duty, and VAT. The declining trend from 2025
                reflects both lower Brent crude prices and KES appreciation.
              </p>
            </div>
          </>
        )}

        {/* ── HORMUZ TAB ── */}
        {activeTab === "hormuz" && (
          <>
            {/* Scenario legend */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Anchor className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800">
                    Scenario A – Strait Open
                  </span>
                </div>
                <p className="text-sm text-emerald-700">
                  Normal maritime traffic through the Strait of Hormuz. Roughly
                  21 million barrels per day (≈ 21% of global oil trade) flow
                  uninterrupted. Prices moderate, supply chains stable.
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-800">
                    Scenario B – Strait Closed
                  </span>
                </div>
                <p className="text-sm text-red-700">
                  A full or partial blockade forces tankers on the Cape of Good
                  Hope route (+10–14 days). Immediate supply shock drives crude
                  prices above $130/bbl within 60 days, triggering energy-driven
                  inflationary pressure globally.
                </p>
              </div>
            </div>

            {/* Price projection chart */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <SectionTitle sub="Projected Brent crude ($/bbl) – May to Dec 2025">
                  Oil Price Projection: Two Scenarios
                </SectionTitle>
                <div className="flex gap-2">
                  <ScenarioBadge scenario="open" />
                  <ScenarioBadge scenario="closed" />
                </div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={straitsProjection}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis
                    domain={[60, 190]}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className="bg-white border border-slate-200 rounded-lg shadow p-3 text-sm">
                          <p className="font-semibold text-slate-700 mb-1">{label}</p>
                          {payload.map((p) => (
                            <p key={p.name} style={{ color: p.stroke }}>
                              {p.name}: <strong>${p.value}/bbl</strong>
                            </p>
                          ))}
                        </div>
                      ) : null
                    }
                  />
                  <Legend />
                  <ReferenceLine
                    y={100}
                    stroke="#ef4444"
                    strokeDasharray="4 4"
                    label={{ value: "$100 threshold", fill: "#ef4444", fontSize: 11 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="baseline"
                    name="Baseline (no change)"
                    stroke="#94a3b8"
                    strokeDasharray="5 5"
                    dot={false}
                    strokeWidth={1.5}
                  />
                  <Line
                    type="monotone"
                    dataKey="open"
                    name="Scenario A – Strait Open"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: "#10b981" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="closed"
                    name="Scenario B – Strait Closed"
                    stroke="#ef4444"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: "#ef4444" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Key facts */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  label: "Oil through Hormuz daily",
                  value: "~21 M bbl",
                  sub: "≈ 21% of global seaborne oil",
                  color: "bg-blue-500",
                  icon: DollarSign,
                },
                {
                  label: "Price spike (closure, 60 days)",
                  value: "+80–120%",
                  sub: "Based on 2011 & 2019 precedents",
                  color: "bg-red-500",
                  icon: TrendingUp,
                },
                {
                  label: "Alternative route delay",
                  value: "+10–14 days",
                  sub: "Cape of Good Hope re-route",
                  color: "bg-amber-500",
                  icon: Globe,
                },
              ].map((s) => (
                <StatCard key={s.label} {...s} />
              ))}
            </div>

            <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
              <Info className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-sm leading-relaxed">
                <strong>Methodology:</strong> Projections combine historical price
                elasticity from the 2011 Hormuz blockade threat, IEA emergency reserve
                drawdown capacity (≈ 1.4 Gb over 90 days), and a Monte Carlo uncertainty
                envelope. The Scenario B curve represents the median of 10,000 simulations;
                the 95th percentile reaches $198/bbl by December 2025.
              </p>
            </div>
          </>
        )}

        {/* ── ECONOMY TAB ── */}
        {activeTab === "economy" && (
          <>
            {/* Sectoral impact */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <SectionTitle sub="Estimated % change in sector profit margins under a sustained high-price environment">
                Sectoral Economic Impact
              </SectionTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={economicImpactData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="sector" tick={{ fontSize: 11 }} />
                  <YAxis
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className="bg-white border border-slate-200 rounded-lg shadow p-3 text-sm">
                          <p className="font-semibold text-slate-700 mb-1">{label}</p>
                          {payload.map((p) => (
                            <p key={p.name} style={{ color: p.fill || p.color }}>
                              {p.name}: <strong>{p.value}%</strong>
                            </p>
                          ))}
                        </div>
                      ) : null
                    }
                  />
                  <Legend />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Bar dataKey="impact" name="Profit margin Δ (%)" radius={[4, 4, 0, 0]}>
                    {economicImpactData.map((entry) => (
                      <Cell
                        key={entry.sector}
                        fill={entry.impact >= 0 ? "#10b981" : "#ef4444"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* GDP & Inflation side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* GDP impact */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
                <SectionTitle sub="Projected GDP growth deviation (pp) by region">
                  GDP Growth Impact
                </SectionTitle>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={gdpImpactProjection} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `${v}pp`}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      dataKey="region"
                      type="category"
                      width={65}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      content={
                        <CustomTooltip unit=" pp" />
                      }
                    />
                    <Legend />
                    <ReferenceLine x={0} stroke="#94a3b8" />
                    <Bar
                      dataKey="open"
                      name="Strait Open"
                      fill="#10b981"
                      radius={[0, 4, 4, 0]}
                    />
                    <Bar
                      dataKey="closed"
                      name="Strait Closed"
                      fill="#ef4444"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Inflation impact */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
                <SectionTitle sub="Projected additional CPI inflation (pp) by region">
                  Inflation Impact
                </SectionTitle>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={inflationProjection} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `+${v}pp`}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      dataKey="region"
                      type="category"
                      width={65}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip unit=" pp" />} />
                    <Legend />
                    <Bar
                      dataKey="open"
                      name="Strait Open"
                      fill="#6366f1"
                      radius={[0, 4, 4, 0]}
                    />
                    <Bar
                      dataKey="closed"
                      name="Strait Closed"
                      fill="#f59e0b"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="flex gap-3 bg-slate-100 border border-slate-200 rounded-xl p-4 text-slate-700">
              <Info className="w-5 h-5 mt-0.5 shrink-0 text-slate-500" />
              <p className="text-sm leading-relaxed">
                <strong>Key takeaway:</strong> A Strait of Hormuz closure would be a
                systemic shock — not merely an energy event. The transmission channels
                include input cost inflation (agriculture, manufacturing), freight rate
                surges (shipping industry), currency pressures in oil-importing emerging
                markets, and central bank policy divergence. Regions with subsidised fuel
                would face fiscal stress, while oil-exporting nations paradoxically face
                supply-route disruption to their own exports.
              </p>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 text-center py-6 text-xs mt-16">
        <p>
          Crude oil &amp; natural gas prices sourced live from{" "}
          {dataSource === "eia" ? "U.S. EIA (Energy Information Administration)" : "World Bank Global Economic Monitor"}.
          Economic projections are illustrative and for analytical purposes only.
        </p>
        <p className="mt-1">© {new Date().getFullYear()} Neuracraft Solutions.</p>
      </footer>
    </div>
  );
}
